import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import ImageWithFallback from './ImageWithFallback';
import { deleteGroup, getGroupTeam, GroupRole, manageGroupTeam, updateGroup } from '../utils/secureFunctions';
import { appConfirm } from '../utils/dialog';

type Member = { uid: string; name: string; username: string; photoURL: string; role: GroupRole };

interface Props {
  group: { id: string; name: string; description: string; category: string; isPrivate: boolean };
  myRole: GroupRole | null;
  onUpdated: (patch: Partial<Props['group']>) => void;
  onClose: () => void;
}

const btn: React.CSSProperties = { padding: '6px 10px', borderRadius: 8, border: '1px solid var(--color-border)', background: 'transparent', color: 'var(--color-text)', fontSize: 12, whiteSpace: 'nowrap', cursor: 'pointer' };
const input: React.CSSProperties = { width: '100%', padding: 10, borderRadius: 8, border: '1px solid var(--color-border)', boxSizing: 'border-box', fontSize: 14, background: 'var(--color-surface)', color: 'var(--color-text)' };
const ROLE_LABEL: Record<GroupRole, string> = { owner: 'Owner', admin: 'Admin', moderator: 'Moderator', member: 'Member' };

const GroupManagePanel: React.FC<Props> = ({ group, myRole, onUpdated, onClose }) => {
  const navigate = useNavigate();
  const isAdmin = myRole === 'owner' || myRole === 'admin';
  const [name, setName] = useState(group.name);
  const [description, setDescription] = useState(group.description);
  const [category, setCategory] = useState(group.category);
  const [isPrivate, setIsPrivate] = useState(!!group.isPrivate);
  const [saving, setSaving] = useState(false);
  const [members, setMembers] = useState<Member[]>([]);
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState<string | null>(null);

  const loadTeam = useCallback(async () => {
    try {
      const res = await getGroupTeam(group.id);
      setMembers(res.members || []);
    } catch (e) {
      console.warn('Failed to load team', e);
    }
  }, [group.id]);

  useEffect(() => { loadTeam(); }, [loadTeam]);

  async function save() {
    if (!name.trim()) return;
    setSaving(true);
    try {
      const patch = { name: name.trim(), description: description.trim(), category: category.trim(), isPrivate };
      await updateGroup(group.id, patch);
      onUpdated(patch);
      alert('Group updated');
    } catch (e: any) {
      alert(e?.message || 'Failed to update group');
    } finally {
      setSaving(false);
    }
  }

  async function remove() {
    if (!await appConfirm(`Delete "${group.name}" permanently? All posts and discussions will be removed.`)) return;
    try {
      await deleteGroup(group.id);
      navigate('/groups');
    } catch (e: any) {
      alert(e?.message || 'Failed to delete group');
    }
  }

  async function act(userId: string, action: 'add' | 'remove', role: 'admin' | 'moderator' | 'member') {
    if (action === 'remove' && role === 'member' && !await appConfirm('Remove this person from the group?')) return;
    setBusy(userId);
    try {
      await manageGroupTeam(group.id, { action, role, userId });
      await loadTeam();
    } catch (e: any) {
      alert(e?.message || 'Action not allowed');
    } finally {
      setBusy(null);
    }
  }

  const shown = members
    .filter((m) => !filter || `${m.name} ${m.username || ''}`.toLowerCase().includes(filter.toLowerCase()))
    .sort((a, b) => ['owner', 'admin', 'moderator', 'member'].indexOf(a.role) - ['owner', 'admin', 'moderator', 'member'].indexOf(b.role));

  return (
    <section style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 12, padding: 16, marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>⚙️ Manage group</h3>
        <span style={{ marginLeft: 8, fontSize: 12, color: 'var(--color-text-muted)' }}>You are {ROLE_LABEL[myRole || 'member']}</span>
        <button onClick={onClose} style={{ ...btn, marginLeft: 'auto' }}>Close</button>
      </div>

      {isAdmin && (
        <div style={{ display: 'grid', gap: 8, marginBottom: 16 }}>
          <input style={input} value={name} onChange={(e) => setName(e.target.value)} placeholder="Group name" maxLength={100} />
          <textarea style={input} value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Description" rows={3} maxLength={2000} />
          <input style={input} value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Category" maxLength={64} />
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input type="checkbox" checked={isPrivate} onChange={(e) => setIsPrivate(e.target.checked)} /> Private group
          </label>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button onClick={save} disabled={saving || !name.trim()} style={{ ...btn, background: 'var(--color-primary-600)', color: '#fff', border: 'none', padding: '8px 14px', fontSize: 13 }}>{saving ? 'Saving…' : 'Save changes'}</button>
            {isAdmin && (
              <button onClick={remove} style={{ ...btn, color: '#B91C1C', borderColor: '#FCA5A5', padding: '8px 14px', fontSize: 13 }}>Delete group</button>
            )}
          </div>
        </div>
      )}

      <h4 style={{ margin: '0 0 8px' }}>Members & roles</h4>
      <input style={{ ...input, marginBottom: 8 }} value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search members" />
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 360, overflowY: 'auto' }}>
        {shown.map((m) => (
          <div key={m.uid} style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
            <ImageWithFallback src={m.photoURL} alt="" style={{ width: 32, height: 32, borderRadius: '50%', objectFit: 'cover' }} />
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>{m.name}</div>
              <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{ROLE_LABEL[m.role]}</div>
            </div>
            {m.role !== 'owner' && (
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {isAdmin && m.role === 'member' && <button disabled={busy === m.uid} style={btn} onClick={() => act(m.uid, 'add', 'moderator')}>Make moderator</button>}
                {isAdmin && m.role !== 'admin' && <button disabled={busy === m.uid} style={btn} onClick={() => act(m.uid, 'add', 'admin')}>Make admin</button>}
                {isAdmin && m.role === 'moderator' && <button disabled={busy === m.uid} style={btn} onClick={() => act(m.uid, 'remove', 'moderator')}>Remove moderator</button>}
                {myRole === 'owner' && m.role === 'admin' && <button disabled={busy === m.uid} style={btn} onClick={() => act(m.uid, 'remove', 'admin')}>Remove admin</button>}
                {(isAdmin ? m.role !== 'admin' || myRole === 'owner' : m.role === 'member') && (
                  <button disabled={busy === m.uid} style={{ ...btn, color: '#B91C1C' }} onClick={() => act(m.uid, 'remove', 'member')}>Remove</button>
                )}
              </div>
            )}
          </div>
        ))}
        {shown.length === 0 && <p style={{ color: 'var(--color-text-muted)', margin: 0 }}>No members found.</p>}
      </div>
    </section>
  );
};

export default GroupManagePanel;
